import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { formatRelativeTimeAgo } from '@confluence/time-ago';

import Button from '@atlaskit/button/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import AddIcon from '@atlaskit/icon/core/add';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import { SidePanel, SidePanelBody, SidePanelFooter, SidePanelHeader } from '@confluence/side-panel';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { RawAction, RawSuggestion } from '../__types__/SpaceSuggestion.ts';

import { SuggestionActionBuilder } from './SuggestionActionBuilder.tsx';
import { SuggestionReviewRow } from './SuggestionReviewRow.tsx';

const i18n = defineMessages({
	title: {
		id: 'space-suggestions.review-panel.title',
		defaultMessage: 'Review suggestion',
		description: 'Title for the suggestion review panel',
	},
	close: {
		id: 'space-suggestions.review-panel.close-label',
		defaultMessage: 'Close',
		description: 'Accessibility label for the close button on the suggestion review panel',
	},
	cancel: {
		id: 'space-suggestions.review-panel.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Label for the cancel button on the suggestion review panel',
	},
	approve: {
		id: 'space-suggestions.review-panel.approve-button',
		defaultMessage: 'Approve',
		description: 'Label for the approve button on the suggestion review panel',
	},
	subtext: {
		id: 'space-suggestions.review-panel.subtext',
		defaultMessage: '{count, plural, one {# suggested change} other {# suggested changes}}',
		description:
			'Subtext shown below the title in the suggestion review panel indicating the number of suggested changes',
	},
	addAction: {
		id: 'space-suggestions.review-panel.add-action-button',
		defaultMessage: 'Add action',
		description:
			'Label for the button at the bottom of the suggestion review panel that opens a form for adding a new action',
	},
	addActionTitle: {
		id: 'space-suggestions.review-panel.add-action-title',
		defaultMessage: 'Add action',
		description:
			'Title shown in the suggestion review panel header while the user is building a new action',
	},
});

export const SUGGESTION_REVIEW_PANEL_WIDTH = 400;

export type SuggestionReviewPanelProps = {
	/** Raw backend suggestion whose actions are displayed for review. */
	rawSuggestion: RawSuggestion;
	onClose: () => void;
	onCancel: () => void;
	onApprove: (acceptedActionStepIds: string[], declinedActionStepIds: string[]) => void;
	isApproveDisabled?: boolean;
	/** Panel width in pixels. Defaults to 400. */
	width?: number;
	/** Total height of banners/headers above the panel, used to offset vertical position. */
	headerHeight?: number;
	/** Renders panel content inside an external sidebar instead of a fixed overlay. */
	isEmbedded?: boolean;
};

export const SuggestionReviewPanel: React.MemoExoticComponent<
	({
		rawSuggestion,
		onClose,
		onCancel,
		onApprove,
		isApproveDisabled,
		width,
		headerHeight,
		isEmbedded,
	}: SuggestionReviewPanelProps) => React.JSX.Element
> = memo(
	({
		rawSuggestion,
		onClose,
		onCancel,
		onApprove,
		isApproveDisabled = false,
		width = SUGGESTION_REVIEW_PANEL_WIDTH,
		headerHeight = 0,
		isEmbedded = false,
	}: SuggestionReviewPanelProps): React.JSX.Element => {
		const intl = useIntl();
		const { formatMessage } = intl;

		const timestamp = formatRelativeTimeAgo(rawSuggestion.createdAt, intl);

		const isAddActionEnabled = StatsigConfigurations.getBoolean(
			'confluence-space-suggestion-add-action',
			false,
		);

		// Which view the panel shows: the read-only review list, or the form for
		// building a new action (the inline "rule builder").
		const [mode, setMode] = useState<'review' | 'build'>('review');

		// Actions authored by the user this session, appended to the backend-provided
		// `rawSuggestion.actions`. Kept separate so the base list stays prop-driven.
		const baseActions = rawSuggestion.actions;
		const [addedActions, setAddedActions] = useState<RawAction[]>([]);
		const reviewActions = useMemo(
			() => [...baseActions, ...addedActions],
			[baseActions, addedActions],
		);
		const actionCount = reviewActions.length;

		const [checkedActions, setCheckedActions] = useState<boolean[]>(() =>
			baseActions.map(() => true),
		);

		// Switching to a different suggestion (the sidebar reuses this component
		// instance) resets the user-authored actions, checked state, and view so no
		// stale state leaks across suggestions.
		const previousIdRef = useRef(rawSuggestion.id);
		useEffect(() => {
			if (previousIdRef.current !== rawSuggestion.id) {
				previousIdRef.current = rawSuggestion.id;
				setAddedActions([]);
				setCheckedActions(baseActions.map(() => true));
				setMode('review');
			}
		}, [rawSuggestion.id, baseActions]);

		const isEveryActionUnchecked = useMemo(
			() => checkedActions.every((isChecked) => !isChecked),
			[checkedActions],
		);
		const handleCheckedChange = useCallback((index: number, isChecked: boolean) => {
			setCheckedActions((currentCheckedActions) =>
				currentCheckedActions.map((currentIsChecked, currentIndex) =>
					currentIndex === index ? isChecked : currentIsChecked,
				),
			);
		}, []);

		const handleAddAction = useCallback((action: RawAction) => {
			setAddedActions((current) => [...current, action]);
			setCheckedActions((current) => [...current, true]);
			setMode('review');
		}, []);

		const isApproveButtonDisabled = isApproveDisabled || isEveryActionUnchecked;

		const handleApprove = useCallback(() => {
			const [acceptedActionStepIds, declinedActionStepIds] = reviewActions.reduce<
				[string[], string[]]
			>(
				([accepted, declined], action, i) => {
					if (action.actionStepId) {
						if (checkedActions[i] ?? true) {
							accepted.push(action.actionStepId);
						} else {
							declined.push(action.actionStepId);
						}
					}
					return [accepted, declined];
				},
				[[], []],
			);
			onApprove(acceptedActionStepIds, declinedActionStepIds);
		}, [reviewActions, checkedActions, onApprove]);

		const isBuilding = isAddActionEnabled && mode === 'build';

		const panelContent = (
			<>
				<SidePanelHeader>
					<Flex direction="row" justifyContent="space-between" alignItems="center">
						<Stack space="space.050">
							<Heading size="small">
								{formatMessage(isBuilding ? i18n.addActionTitle : i18n.title)}
							</Heading>
							{!isBuilding && (
								<Text size="small" color="color.text.subtlest">
									{formatMessage(i18n.subtext, { count: actionCount })} • {timestamp}
								</Text>
							)}
						</Stack>
						<IconButton
							icon={CrossIcon}
							label={formatMessage(i18n.close)}
							appearance="subtle"
							onClick={onClose}
						/>
					</Flex>
				</SidePanelHeader>

				{isBuilding ? (
					<SuggestionActionBuilder
						suggestionId={rawSuggestion.id}
						nextActionIndex={actionCount + 1}
						onAdd={handleAddAction}
						onBack={() => setMode('review')}
					/>
				) : (
					<>
						<SidePanelBody>
							<Stack space="space.100">
								{reviewActions.map((action, index) => (
									<SuggestionReviewRow
										key={`${rawSuggestion.id}-${index}`}
										action={action}
										index={index}
										isChecked={checkedActions[index] ?? true}
										onCheckedChange={handleCheckedChange}
									/>
								))}
								{isAddActionEnabled && (
									<Pressable xcss={styles.addActionButton} onClick={() => setMode('build')}>
										<Flex alignItems="center" gap="space.100">
											<AddIcon label="" size="small" color="currentColor" />
											<Text>{formatMessage(i18n.addAction)}</Text>
										</Flex>
									</Pressable>
								)}
							</Stack>
						</SidePanelBody>

						<SidePanelFooter>
							<Flex direction="row" gap="space.100" justifyContent="end">
								<Button onClick={onCancel}>{formatMessage(i18n.cancel)}</Button>
								<Button
									appearance="primary"
									onClick={handleApprove}
									isDisabled={isApproveButtonDisabled}
								>
									{formatMessage(i18n.approve)}
								</Button>
							</Flex>
						</SidePanelFooter>
					</>
				)}
			</>
		);

		if (isEmbedded) {
			return <Box xcss={styles.embeddedPanel}>{panelContent}</Box>;
		}

		return (
			<SidePanel isFixed width={width} headerHeight={headerHeight} panelTop={headerHeight}>
				{panelContent}
			</SidePanel>
		);
	},
);

const styles = cssMap({
	embeddedPanel: {
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
	},
	// Mirrors the card styling of SuggestionReviewRow so the add affordance reads
	// as a peer of the action rows above it.
	addActionButton: {
		width: '100%',
		textAlign: 'left',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		color: token('color.text.subtle'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
});
